import React, { useId } from 'react';

/**
 * Patrimony brand mark: a "P" built from tapered gradient streaks, ported from
 * patrimony-app/src/components/logo.tsx (same paths, same viewBox). The app's
 * gradient has 22 sampled stops; the five here are the same ones its CSS
 * `.brand-gradient` uses (coral -> red -> pink -> violet -> blue), which reads
 * identically at button size.
 */
const STREAKS = [
  'M 415.0,244.0 L 809.0,234.0 Q 815.0,234.0 815.0,244.0 Q 815.0,254.0 809.0,254.0 Z',
  'M 400.0,272.0 L 838.0,262.0 Q 844.0,262.0 844.0,272.0 Q 844.0,282.0 838.0,282.0 Z',
  'M 400.0,300.5 L 858.0,290.0 Q 864.0,290.0 864.0,300.5 Q 864.0,311.0 858.0,311.0 Z',
  'M 400.0,328.5 L 871.0,318.0 Q 877.0,318.0 877.0,328.5 Q 877.0,339.0 871.0,339.0 Z',
  'M 400.0,356.5 L 880.0,346.0 Q 886.0,346.0 886.0,356.5 Q 886.0,367.0 880.0,367.0 Z',
  'M 400.0,385.0 L 558.0,375.0 Q 564.0,375.0 564.0,385.0 Q 564.0,395.0 558.0,395.0 Z',
  'M 721.0,385.0 L 886.0,375.0 Q 892.0,375.0 892.0,385.0 Q 892.0,395.0 886.0,395.0 Z',
  'M 400.0,414.0 L 557.0,404.0 Q 563.0,404.0 563.0,414.0 Q 563.0,424.0 557.0,424.0 Z',
  'M 721.0,414.0 L 888.0,404.0 Q 894.0,404.0 894.0,414.0 Q 894.0,424.0 888.0,424.0 Z',
  'M 400.0,443.0 L 557.0,433.0 Q 563.0,433.0 563.0,443.0 Q 563.0,453.0 557.0,453.0 Z',
  'M 721.0,443.0 L 888.0,433.0 Q 894.0,433.0 894.0,443.0 Q 894.0,453.0 888.0,453.0 Z',
  'M 400.0,471.5 L 557.0,461.0 Q 563.0,461.0 563.0,471.5 Q 563.0,482.0 557.0,482.0 Z',
  'M 721.0,471.5 L 888.0,461.0 Q 894.0,461.0 894.0,471.5 Q 894.0,482.0 888.0,482.0 Z',
  'M 400.0,500.0 L 558.0,490.0 Q 564.0,490.0 564.0,500.0 Q 564.0,510.0 558.0,510.0 Z',
  'M 721.0,500.0 L 887.0,490.0 Q 893.0,490.0 893.0,500.0 Q 893.0,510.0 887.0,510.0 Z',
  'M 400.0,528.5 L 558.0,518.0 Q 564.0,518.0 564.0,528.5 Q 564.0,539.0 558.0,539.0 Z',
  'M 721.0,528.5 L 882.0,518.0 Q 888.0,518.0 888.0,528.5 Q 888.0,539.0 882.0,539.0 Z',
  'M 400.0,557.0 L 875.0,547.0 Q 881.0,547.0 881.0,557.0 Q 881.0,567.0 875.0,567.0 Z',
  'M 400.0,585.5 L 863.0,575.0 Q 869.0,575.0 869.0,585.5 Q 869.0,596.0 863.0,596.0 Z',
  'M 400.0,614.0 L 846.0,604.0 Q 852.0,604.0 852.0,614.0 Q 852.0,624.0 846.0,624.0 Z',
  'M 400.0,642.0 L 824.0,632.0 Q 830.0,632.0 830.0,642.0 Q 830.0,652.0 824.0,652.0 Z',
  'M 400.0,670.5 L 800.0,661.0 Q 806.0,661.0 806.0,670.5 Q 806.0,680.0 800.0,680.0 Z',
  'M 400.0,699.0 L 561.6,693.0 Q 567.0,693.0 567.0,699.0 Q 567.0,705.0 561.6,705.0 Z',
  'M 400.0,727.5 L 561.1,721.0 Q 567.0,721.0 567.0,727.5 Q 567.0,734.0 561.1,734.0 Z',
  'M 400.0,756.0 L 561.6,750.0 Q 567.0,750.0 567.0,756.0 Q 567.0,762.0 561.6,762.0 Z',
  'M 400.0,784.5 L 561.1,778.0 Q 567.0,778.0 567.0,784.5 Q 567.0,791.0 561.1,791.0 Z',
  'M 400.0,813.0 L 561.6,807.0 Q 567.0,807.0 567.0,813.0 Q 567.0,819.0 561.6,819.0 Z',
  'M 400.0,842.0 L 561.6,836.0 Q 567.0,836.0 567.0,842.0 Q 567.0,848.0 561.6,848.0 Z',
];

export default function PatrimonyMark({ size = 18, className }) {
  const gradId = useId();
  return (
    <svg
      width={size * (540 / 640)}
      height={size}
      viewBox="380 220 540 640"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradId} gradientUnits="userSpaceOnUse" x1="0" y1="234" x2="0" y2="848">
          <stop offset="1.6%" stopColor="#fd5409" />
          <stop offset="29.3%" stopColor="#f9333e" />
          <stop offset="52.6%" stopColor="#fb2f88" />
          <stop offset="75.7%" stopColor="#a740f4" />
          <stop offset="99%" stopColor="#436af7" />
        </linearGradient>
      </defs>
      <g fill={`url(#${gradId})`}>
        {STREAKS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    </svg>
  );
}
